/*
 * "Copy setup runbook": the current setup as Markdown, for a ticket, a handover or a chat. Built only from what the page
 * already read (no extra request) and never with a secret: tokens appear as counts, env vars as key counts.
 */
import type { App, Project } from '@hangar/shared';
import type { Profile, ProviderAccount } from '@/api/schemas';
import { buildSetupText } from '@/features/apps/appsTableModel';
import { teamKeys, type EnvTile } from './setupModel';
import { plural } from './stepNotesModel';
import { STEP_ORDER, STEP_TITLE, stepNumber, statusLabel, type Progress, type ReadinessItem, type StepState } from './steps';

export interface RunbookInput {
  org: string | undefined;
  project: Project | undefined;
  app: App | undefined;
  projects: Project[];
  apps: App[];
  accounts: ProviderAccount[];
  profiles: Profile[];
  envTiles: EnvTile[];
  states: StepState[];
  progress: Progress;
  readiness: ReadinessItem[];
  consoleUrl: string;
  generatedAt: Date;
  /** A platform operator: the runbook names the provider accounts and ids; a client's never does (SPEC decision 23). */
  seesProviders: boolean;
}

const code = (s: string) => `\`${s}\``;
const list = (items: string[], empty: string) => (items.length ? items.map((i) => `- ${i}`).join('\n') : `- ${empty}`);

const accountLine = (a: ProviderAccount): string =>
  `${a.kind === 'bitrise' ? 'Bitrise' : 'Expo EAS'} · ${a.name} (${code(a.externalAccount ?? a.kind)}) · ${plural(a.concurrencyLimit, 'slot', 'slots')} · token ${a.credentialId ? 'stored in Hangar' : 'from the worker env'} · ${a.status}`;

const projectLine = (p: Project, selected: boolean): string => {
  const environments = p.environments.filter((e) => !e.archivedAt).map((e) => e.slug).join(', ') || 'no environments';
  return `${p.name} (${code(p.slug)}) · ${environments}${selected ? ' · selected' : ''}`;
};

/** A client's build service line: ready (with its slots) or on its way, never the accounts behind it. */
const clientServiceLines = (accounts: ProviderAccount[]): string[] => {
  const usable = accounts.filter((a) => a.status !== 'disabled');
  if (usable.length === 0) return [];
  return [`Build service ready · ${plural(usable.reduce((n, a) => n + a.concurrencyLimit, 0), 'slot', 'slots')}`];
};

const appLine = (a: App, selected: boolean, sees: boolean): string => {
  const setup = sees ? `EAS project ${a.easProjectId ? code(a.easProjectId) : 'not linked'}` : `build setup ${buildSetupText(a).toLowerCase()}`;
  return `${a.name} (${code(a.slug)}) · ${a.framework} · working directory ${code(a.workingDirectory)} · ${setup}${selected ? ' · selected' : ''}`;
};

const profileLine = (p: Profile, envName: (id: string) => string, sees: boolean): string => {
  const eas = typeof p.providerConfig.profile === 'string' ? code(p.providerConfig.profile) : '—';
  const environments = p.environments.map((b) => envName(b.environmentId)).join(', ') || 'no environments';
  return `${p.name} (${code(p.slug)}) · ${p.platform} ${p.artifactKind} · ${sees ? 'EAS profile' : 'eas.json profile'} ${eas} · ${p.executionModes.join(', ')} · ${environments} · timeout ${p.timeoutMinutes} min`;
};

const tileLine = (t: EnvTile): string => {
  const { resolution } = t;
  const missing = resolution?.missing.length ? ` · missing ${resolution.missing.map((m) => code(m.key)).join(', ')}` : '';
  return `${t.environment.slug} (${t.platform}): ${resolution ? `${teamKeys(resolution)} keys${missing}` : 'not read'}`;
};

const readinessLine = (i: ReadinessItem): string => `- [${i.ok ? 'x' : ' '}] ${i.label}${i.detail ? `: ${i.detail}` : ''}`;

const title = (r: RunbookInput): string => `# Hangar setup runbook${r.project ? ` · ${r.project.name}` : ''}${r.app ? ` · ${r.app.name}` : ''}`;

const summary = (r: RunbookInput): string =>
  `${r.org ? `${r.org} · ` : ''}${r.progress.done} / ${r.progress.tracked} steps done (${r.progress.percent}%). Generated from ${r.consoleUrl}/getting-started on ${r.generatedAt.toISOString().slice(0, 16).replace('T', ' ')} UTC.`;

/** The "## N. Title · Status" line of step `i` (0-based, in STEP_ORDER). */
function stepHeading(r: RunbookInput, i: number): string {
  const id = STEP_ORDER[i]!;
  return `## ${stepNumber(id)}. ${STEP_TITLE[id]} · ${statusLabel(r.states[i] ?? { id, status: 'todo' })}`;
}

/** The Markdown of the six steps, in order: a heading and its lines. */
function stepSections(r: RunbookInput): string[] {
  const envName = (id: string) => r.project?.environments.find((e) => e.id === id)?.slug ?? id.slice(0, 8);
  return [
    stepHeading(r, 0),
    r.seesProviders
      ? list(r.accounts.map(accountLine), 'No build provider yet: Malible sets up the Expo and Bitrise accounts when the organization is created.')
      : list(clientServiceLines(r.accounts), 'No build service yet: your Hangar administrators set it up when the organization is created.'),
    stepHeading(r, 1),
    list(r.projects.map((p) => projectLine(p, p.id === r.project?.id)), 'No project yet.'),
    stepHeading(r, 2),
    list(r.apps.map((a) => appLine(a, a.id === r.app?.id, r.seesProviders)), r.project ? `No app in ${r.project.name} yet.` : 'Create the project first.'),
    stepHeading(r, 3),
    list(r.profiles.map((p) => profileLine(p, envName, r.seesProviders)), r.app ? `No build profile for ${r.app.name} yet.` : 'Connect the app first.'),
    stepHeading(r, 4),
    list(r.envTiles.map(tileLine), 'No environment to resolve.'),
    stepHeading(r, 5),
    '- Start builds from the console with Run build; they appear under Builds.',
  ];
}

export function setupRunbook(r: RunbookInput): string {
  const sections = [title(r), summary(r), ...stepSections(r), '## Readiness', r.readiness.map(readinessLine).join('\n')];
  return `${sections.join('\n\n')}\n`;
}
